<script lang="ts">
  import { AlertCircle, Clock, HardDrive } from 'lucide-svelte';
  import { fetchRelayMap, type RelayMapResponse } from '$lib/api/rcu_mappings';

  const { unitId }: { unitId: number } = $props();

  let data = $state<RelayMapResponse | null>(null);
  let loading = $state(false);
  let error = $state<string | null>(null);

  async function load() {
    if (!unitId || unitId <= 0) return;
    loading = true;
    error = null;
    try {
      data = await fetchRelayMap(unitId);
    } catch (e: unknown) {
      error = (e as Error).message || 'Failed to load relay map';
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    void unitId;
    load();
  });
</script>

<section class="border border-border-default rounded p-3">
  <div class="flex items-center gap-2 mb-3">
    <HardDrive class="w-4 h-4 text-text-secondary" />
    <h3 class="text-base font-medium text-text-primary">Relay Map</h3>
    <span class="text-2xs text-text-muted ml-auto">Verify against the panel before export</span>
  </div>

  {#if loading}
    <div class="flex items-center gap-2 text-sm text-text-muted py-4">
      <Clock class="w-4 h-4 animate-spin" />
      <span>Loading relay map…</span>
    </div>
  {:else if error}
    <div class="flex items-center gap-2 text-sm text-red-600 py-2">
      <AlertCircle class="w-4 h-4" />
      <span>{error}</span>
    </div>
  {:else if data}
    {#if data.rcus.length === 0 && data.din_modules.length === 0}
      <p class="text-sm text-text-muted">No controllers configured.</p>
    {/if}

    {#each data.rcus as rcu (rcu.rcu_id)}
      <div class="mb-3">
        <p class="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-1">
          {rcu.rcu_name}
        </p>
        {#if rcu.channels.length === 0}
          <p class="text-xs text-text-muted pl-2">No channels.</p>
        {:else}
          <div class="space-y-0.5">
            {#each rcu.channels as ch (ch.channel_number)}
              <div
                class="flex items-center gap-2 py-0.5 px-2 rounded text-sm {ch.load_id
                  ? ''
                  : 'opacity-50'}"
              >
                <span class="font-mono text-xs w-8 shrink-0 text-text-muted">R{ch.channel_number}</span>
                {#if ch.load_name}
                  <span class="text-text-primary">{ch.load_name}</span>
                  {#if ch.room_name}
                    <span class="text-text-muted text-xs">({ch.room_name})</span>
                  {/if}
                {:else}
                  <span class="text-text-muted italic">Unassigned</span>
                {/if}
              </div>
            {/each}
          </div>
        {/if}
      </div>
    {/each}

    {#each data.din_modules as din (din.din_module_id)}
      <div class="mb-3">
        <p class="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-1">
          DIN: {din.din_name}
        </p>
        {#if din.channels.length === 0}
          <p class="text-xs text-text-muted pl-2">No channels.</p>
        {:else}
          <div class="space-y-0.5">
            {#each din.channels as ch (ch.channel_number)}
              <div
                class="flex items-center gap-2 py-0.5 px-2 rounded text-sm {ch.load_id
                  ? ''
                  : 'opacity-50'}"
              >
                <span class="font-mono text-xs w-8 shrink-0 text-text-muted">D{ch.channel_number}</span>
                {#if ch.load_name}
                  <span class="text-text-primary">{ch.load_name}</span>
                  {#if ch.room_name}
                    <span class="text-text-muted text-xs">({ch.room_name})</span>
                  {/if}
                {:else}
                  <span class="text-text-muted italic">Unassigned</span>
                {/if}
              </div>
            {/each}
          </div>
        {/if}
      </div>
    {/each}
  {/if}
</section>
